import { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { costosFijosAPI } from '../services/api';
import { Modal, Button } from './ui';

/**
 * Precios por período de un costo fijo.
 *
 * Un mismo costo fijo (ej. "Hosting 25GB") puede costar distinto según la
 * recurrencia. Cada período se activa por separado y guarda su propio precio;
 * desactivarlo conserva el precio para reactivarlo después.
 *
 * Sin ningún período activo, la cotización usa el precio único del costo fijo.
 *
 * Props:
 *   - open, onClose, onSaved → control del modal y aviso al padre
 *   - costoFijo → { id, nombre, costo }
 *   - periodos  → catálogo de períodos (servicio_periodos)
 */
const CostoFijoPeriodosModal = ({ open, onClose, onSaved, costoFijo, periodos = [] }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [estado, setEstado] = useState({});

    const periodosDisponibles = useMemo(
        () => periodos.filter(p => p.activo !== false),
        [periodos]
    );

    const precioUnico = parseFloat(costoFijo?.costo) || 0;

    useEffect(() => {
        if (!open || !costoFijo?.id) return;

        let cancelado = false;
        const cargar = async () => {
            setLoading(true);
            try {
                const res = await costosFijosAPI.getPrecios(costoFijo.id);
                if (cancelado) return;

                const inicial = {};
                (res.data || []).forEach(p => {
                    inicial[p.periodo_id] = {
                        activo: p.activo !== false,
                        precio: parseFloat(p.precio) || 0
                    };
                });
                setEstado(inicial);
            } catch {
                if (!cancelado) toast.error('Error al cargar los períodos');
            } finally {
                if (!cancelado) setLoading(false);
            }
        };

        cargar();
        return () => { cancelado = true; };
    }, [open, costoFijo?.id]);

    // Al activar por primera vez arrancamos con el precio único como referencia
    const toggle = (periodoId) => {
        setEstado(prev => {
            const actual = prev[periodoId];
            if (actual?.activo) {
                return { ...prev, [periodoId]: { ...actual, activo: false } };
            }
            return {
                ...prev,
                [periodoId]: {
                    activo: true,
                    precio: actual?.precio ?? precioUnico
                }
            };
        });
    };

    const setPrecio = (periodoId, valor) => {
        setEstado(prev => ({
            ...prev,
            [periodoId]: { ...(prev[periodoId] || { activo: true }), precio: valor }
        }));
    };

    const activos = periodosDisponibles.filter(p => estado[p.id]?.activo);

    const guardar = async () => {
        setSaving(true);
        try {
            // Mandamos también los desactivados que tienen precio, para no perderlo
            const precios = Object.entries(estado)
                .filter(([, v]) => v.activo || (parseFloat(v.precio) || 0) > 0)
                .map(([periodoId, v]) => ({
                    periodo_id: parseInt(periodoId, 10),
                    precio: parseFloat(v.precio) || 0,
                    activo: !!v.activo
                }));

            await costosFijosAPI.savePrecios(costoFijo.id, precios);
            toast.success('Períodos actualizados');
            if (onSaved) onSaved();
            onClose();
        } catch (err) {
            toast.error(err.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={`Períodos — ${costoFijo?.nombre || ''}`}
            size="lg"
            footer={
                <div className="flex justify-end gap-2 w-full">
                    <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
                    <Button variant="primary" onClick={guardar} disabled={saving || loading}>
                        {saving ? 'Guardando…' : 'Guardar'}
                    </Button>
                </div>
            }
        >
            <div className="flex flex-col gap-4">
                <p className="text-xs text-muted">
                    Activa los períodos con los que se vende este costo fijo y define el precio de cada uno.
                    Sin ninguno activo, la cotización usa el precio único: <strong>{precioUnico.toFixed(2)}</strong>.
                </p>

                {loading ? (
                    <div className="text-center text-muted py-4 text-sm">Cargando…</div>
                ) : periodosDisponibles.length === 0 ? (
                    <div className="text-center text-muted py-6 text-sm">
                        No hay períodos creados todavía. Créalos en el panel de Períodos.
                    </div>
                ) : (
                    <div className="flex flex-col gap-1.5">
                        {periodosDisponibles.map(p => {
                            const on = !!estado[p.id]?.activo;
                            return (
                                <div
                                    key={p.id}
                                    className={`flex items-center gap-3 p-2 rounded-lg border bg-surface transition ${on ? 'border-primary' : 'border-line'}`}
                                >
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={on}
                                        aria-label={`Activar período ${p.nombre}`}
                                        className={`toggle-switch ${on ? 'is-on' : ''}`}
                                        onClick={() => toggle(p.id)}
                                    >
                                        <span className="toggle-switch-track" aria-hidden="true">
                                            <span className="toggle-switch-thumb" />
                                            <span className="toggle-switch-opt toggle-switch-opt-on">SÍ</span>
                                            <span className="toggle-switch-opt toggle-switch-opt-off">NO</span>
                                        </span>
                                    </button>

                                    <span className="flex-1 text-sm text-text font-medium">{p.nombre}</span>

                                    {on ? (
                                        <label className="field-inline">
                                            <span className="label">Precio:</span>
                                            <input
                                                className="input input-sm"
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={estado[p.id]?.precio ?? ''}
                                                onChange={(e) => setPrecio(p.id, e.target.value)}
                                                autoFocus
                                            />
                                        </label>
                                    ) : (
                                        <span className="text-[11px] text-muted">
                                            {(parseFloat(estado[p.id]?.precio) || 0) > 0
                                                ? `Desactivado — guarda ${(parseFloat(estado[p.id].precio)).toFixed(2)}`
                                                : 'Desactivado'}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Resumen de lo que quedará configurado */}
                <div>
                    <div className="text-xs text-muted font-medium uppercase tracking-wide mb-2">
                        Precios activos
                    </div>
                    {activos.length === 0 ? (
                        <p className="text-sm text-muted">
                            Ninguno. Se usará el precio único de <strong>{precioUnico.toFixed(2)}</strong>.
                        </p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {activos.map(p => (
                                <span key={p.id} className="periodo-chip">
                                    {p.nombre}
                                    <strong>{(parseFloat(estado[p.id]?.precio) || 0).toFixed(2)}</strong>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default CostoFijoPeriodosModal;
